declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      execute: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!;

// Distinguishable failure reasons so the caller can react appropriately:
// - SCRIPT_BLOCKED: api.js never loaded (Brave Shields / ad blocker / extension).
//   Nothing the widget config can fix; show the user a message to allow
//   challenges.cloudflare.com.
// - CHALLENGE_FAILED: the widget loaded but the challenge errored.
// - TIMEOUT: no token within the deadline.
export type TurnstileErrorReason = "SCRIPT_BLOCKED" | "CHALLENGE_FAILED" | "TIMEOUT";

export class TurnstileError extends Error {
  constructor(
    public readonly reason: TurnstileErrorReason,
    message: string,
  ) {
    super(message);
    this.name = "TurnstileError";
  }
}

let widgetId: string | null = null;
let containerEl: HTMLDivElement | null = null;

// Create a real, on-page container. NOT display:none — an interactive
// challenge needs somewhere to render. Positioned fixed bottom-right so it
// stays out of the way until it's actually needed. Adjust to taste.
function ensureContainer(): void {
  if (containerEl) return;

  containerEl = document.createElement("div");
  containerEl.setAttribute("data-turnstile-container", "");
  containerEl.style.position = "fixed";
  containerEl.style.bottom = "16px";
  containerEl.style.right = "16px";
  containerEl.style.zIndex = "2147483647";
  document.body.appendChild(containerEl);
}

export async function getTurnstileToken(): Promise<string> {
  if (typeof window === "undefined") {
    throw new TurnstileError("SCRIPT_BLOCKED", "Turnstile is only available in the browser");
  }

  await waitForTurnstile();
  ensureContainer();

  if (!window.turnstile || !containerEl) {
    throw new TurnstileError("SCRIPT_BLOCKED", "Turnstile failed to initialize");
  }

  return new Promise<string>((resolve, reject) => {
    let settled = false;

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new TurnstileError("TIMEOUT", "Turnstile token request timed out"));
    }, 30000); // interactive challenges take time; a tight 15s starves flagged users

    // Tear down any previous widget and render a fresh one. remove-then-render
    // is enough on its own — no reset needed, which only fires a stale callback.
    if (widgetId !== null) {
      try {
        window.turnstile!.remove(widgetId);
      } catch {
        /* widget already gone */
      }
      widgetId = null;
    }

    widgetId = window.turnstile!.render(containerEl!, {
      sitekey: SITE_KEY,
      appearance: "interaction-only", // silent unless a challenge is required
      // deliberately no `size: "invisible"` and no forced execution — let the
      // dashboard's Managed mode decide the verification method per visitor.
      callback: (token: string) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        resolve(token);
      },
      "error-callback": () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        reject(new TurnstileError("CHALLENGE_FAILED", "Turnstile challenge failed"));
      },
      "timeout-callback": () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        reject(new TurnstileError("TIMEOUT", "Turnstile challenge timed out"));
      },
    });
  });
}

// Resolves once window.turnstile is available. Rejects with SCRIPT_BLOCKED if
// api.js never loads — the signal that Shields/an extension killed the script.
function waitForTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      clearInterval(interval);
      reject(new TurnstileError("SCRIPT_BLOCKED", "Turnstile script failed to load (challenges.cloudflare.com may be blocked)"));
    }, 10000);

    const interval = setInterval(() => {
      if (window.turnstile) {
        clearTimeout(timeout);
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });
}
